<template>
  <AppFooter :color="footerBackgroundColor" @click="toggleVersionInfo">
    <div class="text-center w-100" :title="appVersionTitle">
      {{ versionStore.appVersion.title }} ({{
        versionStore.appVersion.environment
      }}) —
      <strong>{{ versionStore.appVersion.version }}</strong>
      <small> released {{ appReleasedOn }}</small>
      <v-chip
        v-if="isOnLocalhost"
        color="primary"
        variant="flat"
        class="ml-2"
        small
      >
        {{ versionStore.appVersion.releaseSignature }}
      </v-chip>
    </div>
  </AppFooter>

  <!-- Version Info Dialog -->
  <v-dialog
    v-model="isVersionInfoShown"
    width="auto"
    :persistent="versionStore.hasApiError"
  >
    <VersionInfoList />
  </v-dialog>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { useTimeAgo } from '@vueuse/core';
  import { useVersionStore } from '@/store/versionStore';
  import AppFooter from '@/components/Layouts/AppFooter.vue';
  import VersionInfoList from '@/components/VersionInfoList.vue';

  const isOnLocalhost = ref(window.location.hostname === 'localhost');
  const isVersionInfoShown = ref(false);
  const versionStore = useVersionStore();

  // Toggle the dialog visibility
  const toggleVersionInfo = () => {
    isVersionInfoShown.value = !isVersionInfoShown.value;
  };

  watch(
    () => versionStore.hasApiError,
    (newValue) => {
      if (newValue) {
        isVersionInfoShown.value = true;
      }
    }
  );

  // Computed property for the "lastUpdated" display
  const appReleasedOn = computed(() => {
    if (versionStore.appVersion.lastUpdated) {
      return useTimeAgo(new Date(versionStore.appVersion.lastUpdated)).value;
    }
    return '';
  });

  // Computed property for the footer's title attribute
  const appVersionTitle = computed(() => {
    if (versionStore.appVersion.title) {
      return `${versionStore.appVersion.title} (${versionStore.appVersion.environment}) released ${appReleasedOn.value}`;
    }
    return 'App Version Info';
  });

  // Dynamic footer background color based on environment
  const footerBackgroundColor = computed(() => {
    const { environment, releaseSignature } = versionStore.appVersion;

    // Error scenarios
    if (versionStore.hasApiError) return 'error';

    const isLiveApi = versionStore.hasApiLive;
    const isStagingApi = versionStore.hasApiStaging;
    const isDevelopmentApi = versionStore.hasApiDevelopment;

    const isLocalhost = isOnLocalhost.value;
    // Error Conditions
    if (
      (isLiveApi && ['Development', 'Staging'].includes(environment)) ||
      (isLocalhost && ['Staging', 'Live'].includes(environment))
    ) {
      return 'error';
    }

    // Warning Conditions
    if (
      (isStagingApi && ['Development', 'Live'].includes(environment)) ||
      (isDevelopmentApi && ['Live', 'Staging'].includes(environment)) ||
      (isLocalhost && ['master', 'main'].includes(releaseSignature))
      // (!isLocalhost && environment === 'Staging')
    ) {
      return 'warning';
    }

    // Safe Staging Condition
    if (!isLocalhost && environment === 'Staging') {
      return 'amber'; // different color for working staging
    }

    // Safe Conditions
    if (
      (isDevelopmentApi && environment === 'Development') ||
      (isLiveApi && environment === 'Live') ||
      (isStagingApi && environment === 'Staging') ||
      (isLocalhost && environment === 'Development') ||
      (!isLocalhost && ['Development', 'Live'].includes(environment))
    ) {
      return '';
    }

    // Fallback
    return 'error';
  });
</script>
